/**
 * CSV取込。
 * ①ファイル選択 → ②列の確認 → ③突合(消込)結果の確認 → ④取込実行 の順に進む。
 * 突合ルールは docs/specs/core-schema.md 3.2(金額一致+日付±5営業日、候補1件なら自動消込)。
 */
import { useState } from 'react';
import Modal from '@core/components/Modal';
import { useAuth } from '@core/auth/AuthProvider';
import { useToast } from '@core/components/Toast';
import { formatYen } from '@core/lib/money';
import { toShortDate } from '@core/lib/date';
import {
  buildRows,
  detectColumns,
  parseCsv,
  readCsvFile,
  type ColumnMap,
  type CsvTable,
  type ParseResult,
} from './csv';
import { buildImportPlan, summarizePlan, type ImportPlanItem } from './matching';
import { commitImportPlan, fetchCashEntries } from './cashRepo';
import type { CashAccount } from '@core/lib/types';

export default function CsvImportPanel({
  accounts,
  defaultAccountId,
  onClose,
  onImported,
}: {
  accounts: CashAccount[];
  defaultAccountId: string;
  onClose: () => void;
  onImported: () => void;
}) {
  const { user } = useAuth();
  const toast = useToast();
  const [cashAccountId, setCashAccountId] = useState(defaultAccountId);
  const [fileName, setFileName] = useState('');
  const [table, setTable] = useState<CsvTable | null>(null);
  const [map, setMap] = useState<ColumnMap | null>(null);
  const [parsed, setParsed] = useState<ParseResult | null>(null);
  const [plan, setPlan] = useState<ImportPlanItem[] | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleFile(file: File | undefined) {
    if (!file) return;
    setBusy(true);
    try {
      const text = await readCsvFile(file);
      const t = parseCsv(text);
      if (t.headers.length === 0) throw new Error('CSVを読み取れませんでした');
      const m = detectColumns(t.headers);
      setFileName(file.name);
      setTable(t);
      setMap(m);
      setParsed(buildRows(t, m));
      setPlan(null);
    } catch (e) {
      toast(e instanceof Error ? e.message : 'CSVを読み取れませんでした');
    } finally {
      setBusy(false);
    }
  }

  function updateMap(patch: Partial<ColumnMap>) {
    if (!table || !map) return;
    const next = { ...map, ...patch };
    setMap(next);
    setParsed(buildRows(table, next));
    setPlan(null);
  }

  /** 既存の予定・確定データと突合して取込計画を作る(この時点では書き込まない) */
  async function analyze() {
    if (!parsed) return;
    setBusy(true);
    try {
      const entries = await fetchCashEntries(cashAccountId, 500);
      const planned = entries.filter((e) => e.status === 'planned');
      const settledHashes = new Set(
        entries.filter((e) => e.status === 'settled' && e.dedup_hash).map((e) => e.dedup_hash as string),
      );
      setPlan(buildImportPlan({ cashAccountId, rows: parsed.rows, planned, settledHashes }));
    } catch (e) {
      toast(e instanceof Error ? e.message : '突合に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  async function commit() {
    if (!plan || !user || !parsed) return;
    setBusy(true);
    try {
      const result = await commitImportPlan({
        cashAccountId,
        fileName,
        items: plan,
        errorRows: parsed.errors.length,
        userId: user.id,
      });
      toast(
        `取り込みました(消込${result.matched}件 / 新規${result.inserted}件 / 重複${result.duplicates}件)`,
      );
      onImported();
      onClose();
    } catch (e) {
      toast(e instanceof Error ? e.message : '取込に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  const summary = plan ? summarizePlan(plan) : null;

  return (
    <Modal onClose={onClose}>
      <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12, flexShrink: 0 }}>
        <h3>CSV取込</h3>
      </div>

      <div style={{ overflow: 'auto' }}>
        <div className="form-grid">
          <div>
            <label>取込先の口座</label>
            <select
              value={cashAccountId}
              onChange={(e) => {
                setCashAccountId(e.target.value);
                setPlan(null);
              }}
            >
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>CSVファイル</label>
            <input
              type="file"
              accept=".csv,text/csv"
              style={{ padding: 7, fontSize: 12 }}
              onChange={(e) => void handleFile(e.target.files?.[0])}
            />
          </div>
        </div>

        {table && map && (
          <>
            <h3 style={{ marginTop: 18 }}>列の対応</h3>
            <p className="hint" style={{ marginBottom: 10 }}>
              ヘッダー名から自動で判定しています。違っていれば選び直してください。
            </p>
            <div className="form-grid">
              <div>
                <label>日付</label>
                <select value={map.date} onChange={(e) => updateMap({ date: Number(e.target.value) })}>
                  {table.headers.map((h, i) => (
                    <option key={i} value={i}>
                      {h || `(${i + 1}列目)`}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label>摘要</label>
                <select
                  value={map.description}
                  onChange={(e) => updateMap({ description: Number(e.target.value) })}
                >
                  {table.headers.map((h, i) => (
                    <option key={i} value={i}>
                      {h || `(${i + 1}列目)`}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label>入金列</label>
                <select
                  value={map.amountIn ?? ''}
                  onChange={(e) =>
                    updateMap({ amountIn: e.target.value === '' ? null : Number(e.target.value) })
                  }
                >
                  <option value="">なし</option>
                  {table.headers.map((h, i) => (
                    <option key={i} value={i}>
                      {h || `(${i + 1}列目)`}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label>出金列</label>
                <select
                  value={map.amountOut ?? ''}
                  onChange={(e) =>
                    updateMap({ amountOut: e.target.value === '' ? null : Number(e.target.value) })
                  }
                >
                  <option value="">なし</option>
                  {table.headers.map((h, i) => (
                    <option key={i} value={i}>
                      {h || `(${i + 1}列目)`}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label>金額列(1列に符号で表す形式のとき)</label>
                <select
                  value={map.amountSigned ?? ''}
                  onChange={(e) =>
                    updateMap({ amountSigned: e.target.value === '' ? null : Number(e.target.value) })
                  }
                >
                  <option value="">なし</option>
                  {table.headers.map((h, i) => (
                    <option key={i} value={i}>
                      {h || `(${i + 1}列目)`}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {parsed && (
              <p className="hint" style={{ marginTop: 10 }}>
                読み取り: {parsed.rows.length}件
                {parsed.errors.length > 0 && ` / 読み取れない行 ${parsed.errors.length}件(${parsed.errors
                  .slice(0, 3)
                  .map((e) => `${e.lineNo}行目`)
                  .join('・')}${parsed.errors.length > 3 ? ' ほか' : ''})`}
              </p>
            )}

            <button
              className="btn-line"
              style={{ marginTop: 12 }}
              onClick={() => void analyze()}
              disabled={busy || !parsed || parsed.rows.length === 0}
            >
              既存データと突合する
            </button>
          </>
        )}

        {plan && summary && (
          <>
            <h3 style={{ marginTop: 20 }}>突合の結果</h3>
            <p className="hint" style={{ marginBottom: 10 }}>
              支払予定と金額が一致し、日付が±5営業日以内の行を自動で消し込みます(新しい行は作らず、
              予定の行を確定に変えます)。候補が複数ある行だけ選んでください。
            </p>
            <table>
              <tbody>
                <tr>
                  <td>自動消込</td>
                  <td className="num">{summary.autoMatched}件</td>
                  <td className="hint">支払予定と一致。二重計上を防ぎます</td>
                </tr>
                <tr>
                  <td>要確認</td>
                  <td className="num">{summary.needsReview}件</td>
                  <td className="hint">候補が複数あるため選択が必要です</td>
                </tr>
                <tr>
                  <td>新規登録</td>
                  <td className="num">{summary.inserted}件</td>
                  <td className="hint">対応する予定がないため新しく登録します</td>
                </tr>
                <tr>
                  <td>重複</td>
                  <td className="num">{summary.duplicates}件</td>
                  <td className="hint">取込済みのため何もしません</td>
                </tr>
              </tbody>
            </table>

            {summary.needsReview > 0 && (
              <>
                <h3 style={{ marginTop: 18 }}>要確認の行</h3>
                <table>
                  <thead>
                    <tr>
                      <th>日付</th>
                      <th>内容</th>
                      <th className="num">金額</th>
                      <th>消込先</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plan
                      .map((item, index) => ({ item, index }))
                      .filter(({ item }) => item.kind === 'needs-review')
                      .map(({ item, index }) => (
                        <tr key={item.row.lineNo}>
                          <td>{toShortDate(item.row.date)}</td>
                          <td>{item.row.description}</td>
                          <td className="num">
                            {item.row.direction === 'in' ? '' : '-'}
                            {formatYen(item.row.amount)}
                          </td>
                          <td>
                            <select
                              value={item.chosenEntryId ?? ''}
                              onChange={(e) => {
                                const next = [...plan];
                                next[index] = {
                                  ...item,
                                  chosenEntryId: e.target.value === '' ? null : e.target.value,
                                };
                                setPlan(next);
                              }}
                            >
                              {item.candidates.map((c) => (
                                <option key={c.id} value={c.id}>
                                  予定 {toShortDate(c.date)} {formatYen(c.amount)}円
                                  {c.description ? ` / ${c.description}` : ''}
                                </option>
                              ))}
                              <option value="">消し込まず新規登録する</option>
                            </select>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </>
            )}
          </>
        )}
      </div>

      {plan && (
        <div style={{ flexShrink: 0, paddingTop: 12 }}>
          <button className="btn-green" style={{ marginTop: 0 }} onClick={() => void commit()} disabled={busy}>
            {busy ? '取込中…' : '取り込む'}
          </button>
        </div>
      )}
    </Modal>
  );
}
